import { ChevronsUpDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Organization } from "@/shared/client";
import useAuth from "@/shared/hooks/use-auth";
import { useTranslations } from "use-intl";

export function OrganizationSwitcher() {
  const { isMobile } = useSidebar();
  const { user, selectedOrganization, setSelectedOrganization, organizations } =
    useAuth();
  const t = useTranslations("sidebar.organization_switcher");

  const handleOrganizationChange = (organization: Organization) => {
    setSelectedOrganization(organization);
  };

  // Show switcher if user exists and has organizations
  const shouldShowSwitcher = user && organizations.length > 0;

  if (!shouldShowSwitcher) {
    return null;
  }

  // For system admins without a selected organization, use first available org as display
  const displayOrganization = selectedOrganization || organizations[0];

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <span className="text-sm font-medium">
                  {displayOrganization?.name.charAt(0).toUpperCase() || "?"}
                </span>
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {displayOrganization?.name || ""}
                </span>
                <span className="truncate text-xs">
                  {displayOrganization?.email || ""}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t("organizations")}
            </DropdownMenuLabel>
            {organizations.map((organization, index) => {
              const isSelected =
                selectedOrganization?.id === organization.id ||
                (!selectedOrganization && index === 0);
              return (
                <DropdownMenuItem
                  key={organization.id}
                  onClick={() => handleOrganizationChange(organization)}
                  className={`gap-2 p-2 ${
                    isSelected ? "bg-accent text-accent-foreground" : ""
                  }`}
                >
                  <div
                    className={`flex size-6 items-center justify-center rounded-md border ${
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary"
                        : ""
                    }`}
                  >
                    <span className="text-xs font-medium">
                      {organization.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">{organization.name}</div>
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
